import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { ConfigurationModel } from '@3ddv/utils-igsu';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { catchError } from 'rxjs';

import { ModalService } from './_shared/data-access/modal.service';
import { PatronData, SessionService } from './_shared/data-access/session.service';
import { HeaderWrapperComponent } from './_shared/feature/header-wrapper/header-wrapper.component';
import { ModalWrapperComponent } from './_shared/feature/modal-wrapper/modal-wrapper.component';
import { CustomHttpInterceptor } from './_shared/interceptors/http-interceptor.interceptor';
import { updateVenueImage } from './_shared/utils/update-venue-image';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing.module';

interface AuthenticateResponse {
  configurations: ConfigurationModel[];
  organization: Record<string, any>;
  token: string;
  patron: PatronData;
}

/**
 * Initializes application with certain parameters fetched from the URL and backend.
 * It's intended to run before the application fully bootstraps.
 * @param http - The HTTP client used for making the request.
 * @returns A function that returns a promise, signaling the app initialization completion.
 */
function initializeApp(
  http: HttpClient,
  route: ActivatedRoute,
  sessionService: SessionService,
  modalService: ModalService
): () => void {
  return () => {
    return route.queryParams.subscribe(queryParameters => {
      const patron = sessionService.getPatron();
      if (patron) {
        return;
      }

      const oktaId = queryParameters['OKTA_ID'];
      const email = queryParameters['email'];
      const venueId = queryParameters['Venue_ID'];

      if (!venueId && (!email || !oktaId)) {
        return;
      }

      const endpoint = `/auth/identify?VENUE_ID=${venueId}&email=${email}&OKTA_ID=${oktaId}`;
      http
        .get<AuthenticateResponse>(endpoint)
        .pipe(catchError((error: unknown) => sessionService.handleAuthenticationError(error, modalService)))
        .subscribe(data => {
          if (!data) {
            return;
          }

          sessionStorage.setItem('jwt', data.token);
          data.patron.venueId = venueId;
          const clubName = data.organization['name'];
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => updateVenueImage(clubName));
          } else {
            updateVenueImage(clubName);
          }

          data.patron.token = data.token;
          // OKTA_ID might come empty, which in that case we just assign it to null
          data.patron.oktaId = oktaId ? oktaId : null;
          data.patron.environment = clubName;
          sessionService.setPatron(data.patron);
          sessionService.setAvailableConfigurations(data.configurations);
        });
    });
  };
}

/**
 * Configuration object for application initialization.
 * This object uses Angular's APP_INITIALIZER token to provide a way to execute custom initialization logic
 * during application startup. The initialization is done by the 'initializeApp' function.
 *
 * */
const appInitObject = provideAppInitializer(() => {
  const initializerFunction = initializeApp(
    inject(HttpClient),
    inject(ActivatedRoute),
    inject(SessionService),
    inject(ModalService)
  );
  return initializerFunction();
});

/**
 * Configuration object for HTTP interceptors.
 * This object tells Angular that the provided class (CustomHttpInterceptor) should be used
 * as an interceptor for all outgoing HTTP requests.
 */
const interceptorObject = {
  provide: HTTP_INTERCEPTORS,
  useClass: CustomHttpInterceptor,
  multi: true,
};

@NgModule({
  imports: [BrowserModule, AppRoutingModule, BrowserAnimationsModule, ModalWrapperComponent, HeaderWrapperComponent],
  declarations: [AppComponent],
  providers: [appInitObject, interceptorObject, provideHttpClient(withInterceptorsFromDi())],
  bootstrap: [AppComponent],
})
export class AppModule {}
